"use client";

import Image from "next/image";
import Link from "next/link";
import {
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Search,
  ArrowRight,
  Menu,
  X,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { isAbsolute } from "path";


const Navbar = () => {
  const pathname = usePathname();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isAboutPage = pathname.startsWith("/about-us");
  const isTeamPage = pathname.startsWith("/team");
  const isPrivacyPolicy = pathname.startsWith("/privacy-policy");
  const isCookiePolicy = pathname.startsWith("/cookie-policy");

  const socialLinks = [
    { platform: "Instagram", href: "https://www.instagram.com/", icon: <Instagram /> },
    { platform: "Facebook", href: "https://www.facebook.com/", icon: <Facebook /> },
    { platform: "Youtube", href: "https://www.youtube.com/", icon: <Youtube /> },
    { platform: "LinkedIn", href: "https://www.linkedin.com/", icon: <Linkedin /> },
  ];

  const navLinks = [
    {
      link: "About Us",
      href: "/about-us",
    },
    { link: "Team", href: "/team" },
    {
      link: "Expertise",
      href: "/expertise",
      submenu: [
        { title: "Corporate and Commercial law", href: "/expertise/corporate-commercial-law/" },
        { title: "Litigation and Dispute Resolution", href: "/expertise/litigation-dispute-resolution/" },
        { title: "Constitutional and Administrative law", href: "/expertise/constitutional-administrative-law/" },
        { title: "Employment and Labour law", href: "/expertise/employment-labour-law/" },
        { title: "Property & Real Estate", href: "/expertise/property-real-estate/" },
        { title: "Intellectual Property", href: "/expertise/intellectual-property/" },
        { title: "Banking and Finance", href: "/expertise/banking-finance/" },
        { title: "Family law", href: "/expertise/family-law/" },
        { title: "Immigration law", href: "/expertise/immigration-law/" }
      ],
    },
    { link: "Insights", href: "/insights" },
    { link: "Careers", href: "/careers" },
    { link: "Media Center", href: "/media-center" },
    { link: "Contact Us", href: "/contact-us" },
  ];

  /** Detect Scroll */
  useEffect(() => {

    if (isAboutPage) {
      setIsScrolled(true);   
      return;               
    }

    if (isTeamPage) {
      setIsScrolled(true);   
      return;               
    }

    if (isPrivacyPolicy) {
      setIsScrolled(true);   
      return;               
    }

    if (isCookiePolicy) {
      setIsScrolled(true);   
      return;               
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isAboutPage, isTeamPage, isPrivacyPolicy, isCookiePolicy]);

  return (
    <header
      className={`fixed top-0 left-0 w-full py-4 md:pt-0 z-50 transition-colors duration-300 ${
        isScrolled || isAboutPage || isTeamPage || isCookiePolicy || isPrivacyPolicy ? "bg-white shadow-lg" : "bg-transparent"
      }`}
    >
      <nav className="relative z-10 flex items-center justify-between w-full px-4 md:px-15">

        {/* Logo */}
        <div className="flex flex-1 w-full">
          <Link href="/">
            <Image
              src={isScrolled || isAboutPage || isTeamPage ? "/blackk-logo.svg" : "/landoo-logo.svg"}
              alt="law-firm-logo"
              width={440}
              height={300}
              className="hidden lg:block object-contain transition-all duration-300"
            />
            <Image
              src={isScrolled ? "/blackk-logo.svg" : "/landoo-logo.svg"}
              alt="law-firm-logo-mobile"
              width={240}
              height={150}
              className="block lg:hidden object-contain transition-all duration-300"
            />
          </Link>
        </div>

        {/* Desktop navigation + search + newsletter */}
        <div className="hidden lg:flex flex-col flex-3 items-center justify-end">
          <div className="flex items-center justify-end w-full pt-2 gap-6">

            {/* Search */}
            <div
              className={`flex items-center gap-2 cursor-pointer transition-colors ${
                isScrolled || isAboutPage || isTeamPage || isCookiePolicy || isPrivacyPolicy ? "text-black" : "text-white"
              }`}
            >
              <Search className="h-5 w-5" />
              <h4 className="text-medium border-b-2">Search</h4>
            </div>

            {/* Social icons */}
            <div className="flex items-center gap-3">
              {socialLinks.map((i, index) => (
                <Link
                  key={index}
                  href={i.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center h-5 w-5 transition-colors ${
                    isScrolled || isAboutPage || isTeamPage || isCookiePolicy || isPrivacyPolicy ? "text-black" : "text-white"
                  }`}
                >
                  {i.icon}
                </Link>
              ))}
            </div>

            {/* Newsletter Button */}
            <Link
              href="/contact-us"
              className="ml-4 border border-secondary px-4 py-2 text-md
              rounded-lg transition-all bg-secondary hover:text-black">
              Request consultation
            </Link>
          </div>

          {/* Primary links */}
          <div className="flex items-center justify-end w-full py-3 gap-12 relative">
            {navLinks.map((item, index) => (
              <div
                key={index}
                className="relative group"
                onMouseEnter={() => item.submenu && setActiveIndex(index)}
                onMouseLeave={() => item.submenu && setActiveIndex(null)}
              >
                <Link
                  href={item.href}
                  className={`text-[18px] font-light transition-colors ${
                    isScrolled || isAboutPage || isTeamPage ? "text-black" : "text-white"
                  }`}
                >
                  {item.link}
                </Link>

                {item.submenu && activeIndex === index && (
                  <div
                    ref={dropdownRef}
                    className="fixed left-0 top-[90px] w-screen z-40 bg-white"
                  >
                    <div className="max-w-[1400px] mx-auto grid grid-cols-2 sm:grid-cols-3 gap-4 py-10 px-20">
                      {item.submenu.map((sub, idx) => (
                        <Link
                          key={idx}
                          href={sub.href}
                          className="flex gap-2 text-gray-700 hover:text-primary text-[18px]"
                        >
                          {sub.title}
                          <ArrowRight className="-rotate-45" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile toggle */}
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
          className={`lg:hidden ${isScrolled || isAboutPage || isTeamPage || isCookiePolicy || isPrivacyPolicy ? "text-black" : "text-white"}`}>
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed top-[100px] left-0 w-full bg-white text-black shadow-md z-50 animate-slideDown">
          <div className="flex flex-col w-full px-6 py-6 gap-6">
            {navLinks.map((item, index) => (
              <div key={index}>
                <div
                  className="text-[18px] font-light text-black flex justify-between cursor-pointer"
                  onClick={() =>
                    item.submenu
                      ? setActiveIndex(activeIndex === index ? null : index)
                      : setIsMobileMenuOpen(false)
                  }
                >
                  <Link href={item.href}>{item.link}</Link>
                  {item.submenu && (
                    <ArrowRight className={`${activeIndex === index ? "rotate-90" : ""} transition`} />
                  )}
                </div>

                {item.submenu && activeIndex === index && (
                  <div className="flex flex-col gap-3 pl-4 mt-3">
                    {item.submenu.map((sub, idx) => (
                      <Link
                        key={idx}
                        href={sub.href}
                        className="text-gray-600 hover:text-black"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {sub.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
